"use client";

import { c as _c } from "react/compiler-runtime";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import type { WavesOverviewParams } from "@/types/waves.types";
import type { ApiWavesOverviewType } from "@/generated/models/ApiWavesOverviewType";
import { commonApiFetch } from "@/services/api/common-api";
import type { ApiWave } from "@/generated/models/ApiWave";
import { QueryKey } from "@/components/react-query-wrapper/ReactQueryWrapper";
import { getDefaultQueryRetry } from "@/components/react-query-wrapper/utils/query-utils";
interface UseWavesOverviewProps {
  readonly type: ApiWavesOverviewType;
  readonly limit?: number | undefined;
  readonly following?: boolean | undefined;
  readonly viewerIdentityKey?: string | null | undefined;
  /**
   * If true, fetch only direct message waves. If false, exclude them. Undefined -> no filter.
   */
  readonly directMessage?: boolean | undefined;
  readonly refetchInterval?: number | undefined;
  readonly refetchIntervalInBackground?: boolean | undefined;
}
export const useWavesOverview = t0 => {
  const $ = _c(50);
  const {
    type,
    limit: t1,
    following: t2,
    viewerIdentityKey,
    directMessage,
    refetchInterval: t3,
    refetchIntervalInBackground: t4
  } = t0;
  const limit = t1 === undefined ? 20 : t1;
  const following = t2 === undefined ? false : t2;
  const refetchInterval = t3 === undefined ? Infinity : t3;
  const refetchIntervalInBackground = t4 === undefined ? false : t4;
  let t5;
  if ($[0] !== directMessage) {
    t5 = directMessage !== undefined ? {
      direct_message: directMessage
    } : {};
    $[0] = directMessage;
    $[1] = t5;
  } else {
    t5 = $[1];
  }
  let t6;
  if ($[2] !== following || $[3] !== limit || $[4] !== t5 || $[5] !== type) {
    t6 = {
      limit,
      type,
      only_waves_followed_by_authenticated_user: following,
      ...t5
    };
    $[2] = following;
    $[3] = limit;
    $[4] = t5;
    $[5] = type;
    $[6] = t6;
  } else {
    t6 = $[6];
  }
  const params = t6;
  let t7;
  if ($[7] !== viewerIdentityKey) {
    t7 = viewerIdentityKey?.trim().toLowerCase() ?? null;
    $[7] = viewerIdentityKey;
    $[8] = t7;
  } else {
    t7 = $[8];
  }
  const normalizedViewerIdentityKey = t7;
  let t8;
  bb0: {
    if (!normalizedViewerIdentityKey) {
      t8 = params;
      break bb0;
    }
    let t9;
    if ($[9] !== normalizedViewerIdentityKey || $[10] !== params) {
      t9 = {
        ...params,
        viewer_identity: normalizedViewerIdentityKey
      };
      $[9] = normalizedViewerIdentityKey;
      $[10] = params;
      $[11] = t9;
    } else {
      t9 = $[11];
    }
    t8 = t9;
  }
  const queryKeyParams = t8;
  const [lastErrorTimestamp, setLastErrorTimestamp] = useState(null);
  let t9;
  if ($[12] !== queryKeyParams) {
    t9 = [QueryKey.WAVES_OVERVIEW, queryKeyParams];
    $[12] = queryKeyParams;
    $[13] = t9;
  } else {
    t9 = $[13];
  }
  let t10;
  if ($[14] !== params) {
    t10 = async t11 => {
      const {
        pageParam
      } = t11;
      const queryParams = {
        limit: `${params.limit}`,
        offset: `${pageParam}`,
        type: params.type,
        only_waves_followed_by_authenticated_user: `${params.only_waves_followed_by_authenticated_user}`
      };
      if (params.direct_message !== undefined) {
        queryParams.direct_message = `${params.direct_message}`;
      }
      return await commonApiFetch({
        endpoint: "waves-overview",
        params: queryParams
      });
    };
    $[14] = params;
    $[15] = t10;
  } else {
    t10 = $[15];
  }
  let t11;
  if ($[16] !== params) {
    t11 = (_, allPages) => allPages.at(-1)?.length === params.limit ? allPages.flat().length : null;
    $[16] = params;
    $[17] = t11;
  } else {
    t11 = $[17];
  }
  let t12;
  if ($[18] !== normalizedViewerIdentityKey) {
    t12 = (previousData, previousQuery) => {
      const previousParams = previousQuery?.queryKey?.[1] as {
        viewer_identity?: string | null;
      } | undefined;
      const previousViewerIdentity = typeof previousParams?.viewer_identity === "string" ? previousParams.viewer_identity : null;
      if (previousViewerIdentity === normalizedViewerIdentityKey) {
        return previousData;
      }
    };
    $[18] = normalizedViewerIdentityKey;
    $[19] = t12;
  } else {
    t12 = $[19];
  }
  let t13;
  if ($[20] === Symbol.for("react.memo_cache_sentinel")) {
    t13 = getDefaultQueryRetry(() => setLastErrorTimestamp(Date.now()));
    $[20] = t13;
  } else {
    t13 = $[20];
  }
  let t14;
  if ($[21] !== refetchInterval || $[22] !== refetchIntervalInBackground || $[23] !== t10 || $[24] !== t11 || $[25] !== t12 || $[26] !== t9) {
    t14 = {
      queryKey: t9,
      queryFn: t10,
      initialPageParam: 0,
      getNextPageParam: t11,
      placeholderData: t12,
      refetchInterval,
      refetchIntervalInBackground,
      ...t13
    };
    $[21] = refetchInterval;
    $[22] = refetchIntervalInBackground;
    $[23] = t10;
    $[24] = t11;
    $[25] = t12;
    $[26] = t9;
    $[27] = t14;
  } else {
    t14 = $[27];
  }
  const query = useInfiniteQuery(t14);
  let t15;
  if ($[28] !== query.data?.pages) {
    t15 = () => query.data?.pages.flat() ?? [];
    $[28] = query.data?.pages;
    $[29] = t15;
  } else {
    t15 = $[29];
  }
  const getWaves = t15;
  let t16;
  if ($[30] !== getWaves) {
    t16 = getWaves();
    $[30] = getWaves;
    $[31] = t16;
  } else {
    t16 = $[31];
  }
  const [waves, setWaves] = useState(t16);
  let t17;
  if ($[32] !== getWaves) {
    t17 = () => {
      setWaves(getWaves());
    };
    $[32] = getWaves;
    $[33] = t17;
  } else {
    t17 = $[33];
  }
  let t18;
  if ($[34] !== query.data) {
    t18 = [query.data];
    $[34] = query.data;
    $[35] = t18;
  } else {
    t18 = $[35];
  }
  useEffect(t17, t18);
  let t19;
  if ($[36] !== lastErrorTimestamp || $[37] !== query) {
    t19 = () => {
      if (lastErrorTimestamp && Date.now() - lastErrorTimestamp < 30000) {
        setTimeout(() => {
          query.fetchNextPage();
        }, 30000);
        return;
      }
      query.fetchNextPage();
    };
    $[36] = lastErrorTimestamp;
    $[37] = query;
    $[38] = t19;
  } else {
    t19 = $[38];
  }
  const fetchNextPage = t19;
  let t20;
  if ($[39] !== lastErrorTimestamp || $[40] !== query) {
    t20 = () => {
      if (lastErrorTimestamp && Date.now() - lastErrorTimestamp < 30000) {
        setTimeout(() => {
          query.refetch();
        }, 30000);
        return;
      }
      query.refetch();
    };
    $[39] = lastErrorTimestamp;
    $[40] = query;
    $[41] = t20;
  } else {
    t20 = $[41];
  }
  const refetch = t20;
  let t21;
  if ($[42] !== fetchNextPage || $[43] !== query.hasNextPage || $[44] !== query.isFetching || $[45] !== query.isFetchingNextPage || $[46] !== query.status || $[47] !== refetch || $[48] !== waves) {
    t21 = {
      waves,
      isFetching: query.isFetching,
      isFetchingNextPage: query.isFetchingNextPage,
      hasNextPage: query.hasNextPage,
      fetchNextPage,
      status: query.status,
      refetch
    };
    $[42] = fetchNextPage;
    $[43] = query.hasNextPage;
    $[44] = query.isFetching;
    $[45] = query.isFetchingNextPage;
    $[46] = query.status;
    $[47] = refetch;
    $[48] = waves;
    $[49] = t21;
  } else {
    t21 = $[49];
  }
  const returnValue = t21;
  return returnValue;
};